import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {navigate} from 'gatsby';

import {jwtDecode} from 'jwt-decode';
import {enqueueSnackbar} from 'notistack';

import {logIn, logOff} from '../api';
import {
  ERROR,
  FAILED_LOGIN,
  PASSWORD_EXPIRES_WARNING_DAYS,
  TOKEN_EXPIRED,
} from '../constants';
import {
  AuthContextValue,
  Authorizations,
  TokenClaimsDto,
  PasswordChangeDialogProps,
  TokenProps,
} from '../models';
import {deleteItem, getValue, setValue} from '../utils';

const noop = () => {};

const AuthContext = React.createContext<AuthContextValue>({
  login: noop,
  loginError: false,
  logout: noop,
  forceLogout: noop,
  updateToken: noop,
  passwordChangeProps: {open: false, passwordExpiresInDays: 360},
  closePWDialog: noop,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [token, setToken] = useState<string | undefined>();
  const [userName, setUserName] = useState<string | undefined>();
  const [tokenLoaded, setTokenLoaded] = useState<boolean>(false);
  const [authorizations, setAuthorizations] = useState<
    Authorizations | undefined
  >();
  const [importMappings, setImportMappings] = useState<string[]>([]);
  const [region, setRegion] = useState<string>('');
  const [loginError, setLoginError] = useState<boolean>(false);
  const [passwordChangeProps, setPasswordChangeProps] =
    useState<PasswordChangeDialogProps>({
      open: false,
      passwordExpiresInDays: 360,
    });
  const isLoggingOut = useRef(false);

  useEffect(() => {
    const tokenFromStorage: TokenProps | undefined = validate(
      getValue('token', null),
    );
    if (tokenFromStorage) {
      setToken(tokenFromStorage.token);
      setTokenStateValues(tokenFromStorage);
    } else {
      setToken(undefined);
    }
    setTokenLoaded(true);
  }, []);

  const validate = (tokenString: string | null) => {
    let tokenDic: TokenProps | undefined = tokenString
      ? JSON.parse(tokenString) || undefined
      : undefined;
    if (!(tokenDic === undefined)) {
      const decodedToken = jwtDecode<TokenClaimsDto>(tokenDic.token);
      const currentDate = new Date();
      if (decodedToken.exp * 1000 < currentDate.getTime()) {
        tokenDic = undefined;
      }
    }
    return tokenDic;
  };

  const setTokenStateValues = (tokenDic: TokenProps) => {
    if (!(tokenDic === undefined)) {
      const decodedToken = jwtDecode<TokenClaimsDto>(tokenDic.token);
      const expireDays = decodedToken.passwordExpiresInDays;
      setUserName(decodedToken.sub);
      setAuthorizations(decodedToken.authorizations);
      setImportMappings(decodedToken.importMappings);
      setRegion(decodedToken.region);
      setPasswordChangeProps({
        open:
          expireDays < PASSWORD_EXPIRES_WARNING_DAYS && !tokenDic.dialogShown,
        passwordExpiresInDays: expireDays,
      });
    }
  };

  const closePWDialog = useCallback(() => {
    setPasswordChangeProps(prev => ({...prev, open: false}));
    setValue('token', JSON.stringify({token: token, dialogShown: true}));
  }, [token]);

  const login = useCallback(
    (credentials: {username: string; password: string}) => {
      logIn(credentials)
        .then(tokenData => {
          setValue('token', JSON.stringify(tokenData));
          setToken(tokenData.token);
          setTokenStateValues(tokenData);
          setLoginError(false);
          isLoggingOut.current = false;
          navigate('/start');
        })
        .catch(err => {
          setLoginError(true);
          if (err?.response?.status === 401) {
            enqueueSnackbar(FAILED_LOGIN, {
              variant: 'error',
              autoHideDuration: 7000,
              preventDuplicate: true,
            });
          } else {
            enqueueSnackbar(ERROR, {variant: 'error'});
          }
        });
    },
    [],
  );

  // Nur eine Logout-Methode soll bei einem Logout aufgerufen werden können, sodass Fehler die durch den Logout entstehen keine unnötigen Nachrichten verursachen.
  // Reset beim erneuten Login bzw. Refresh der Seite.
  const forceLogout = useCallback(() => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    enqueueSnackbar(TOKEN_EXPIRED, {variant: 'error'});
    deleteItem('token');
    setToken(undefined);
    navigate('/');
  }, []);
  const logout = useCallback(() => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    if (token) {
      logOff(token);
    }
    deleteItem('token');
    setToken(undefined);
    navigate('/');
  }, [token]);

  /** Aktualisiert den Token im Auth-Kontext und im LocalStorage nach einer Passwortänderung. */
  const updateToken = useCallback((tokenData: TokenProps) => {
    setValue('token', JSON.stringify(tokenData));
    setToken(tokenData.token);
    setTokenStateValues(tokenData);
  }, []);

  const value = useMemo(
    () => ({
      token,
      userName,
      authorizations,
      importMappings,
      region,
      login,
      loginError,
      logout,
      forceLogout,
      updateToken,
      passwordChangeProps,
      closePWDialog,
    }),
    [
      token,
      userName,
      authorizations,
      importMappings,
      region,
      login,
      loginError,
      logout,
      forceLogout,
      updateToken,
      passwordChangeProps,
      closePWDialog,
    ],
  );

  if (!tokenLoaded) {
    return <></>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
